/**
 * The mint-a-key form (Feature: terraform-provider, task 7.2; Requirement 11.4; Design: §Components 9,
 * §Security and Multi-Tenancy finding 3).
 *
 * Structured like {@link import('./InviteMemberForm').InviteMemberForm}: the form owns its draft, the
 * page owns {@link import('../lib/use-write').useWrite}, and validation stays with the API so there is
 * one validator rather than two that disagree. A blank `name`, an unknown `role`, or a
 * `expiresInDays` that is not a positive integer therefore arrive as `400 validation_failed` field
 * errors and land beside the input through {@link FormField}.
 *
 * ## The role default is a security control, not a convenience
 *
 * `role` defaults to `editor`, and the helper text says why. Terraform needs `check:*` and
 * `alert-rule:*`, which `editor` grants. An `admin` key additionally holds `api-key:create` — and the
 * key routes require no User principal (`api/api-keys.ts` imports no `requireUserPrincipal`), so an
 * `admin` key can mint further keys entirely on its own, with no human in the loop. That is the
 * requirements' Security finding 3, and this default plus this sentence are its mitigation. Anyone
 * changing the default upward should read that finding first.
 *
 * ## The error paths are unprefixed, unlike every other form in the dashboard
 *
 * `check.spec.target` and `alertRule.maxNotificationRate` are rooted at an entity because those routes
 * validate a nested entity. The key routes validate a flat body, so `handleCreateApiKey` reports
 * `name`, `role`, and `expiresInDays` bare. Prefixing them here would silently orphan every field
 * error into the form-level panel.
 */

import { useState, type FormEvent } from 'react';

import type { FieldError } from '@soroush/core';

import type { CreateApiKeyPayload, MemberRole } from '../api/types';
import { MEMBER_ROLES } from '../api/types';

import { FormErrorPanel, FormField, unmappedFieldErrors } from './FieldErrorText';
import { ROLE_LABELS } from './SharingDisclosure';

/** The error paths this form renders beside an input; anything else goes to the form-level panel. */
const MAPPED_PATHS = ['name', 'role', 'expiresInDays'];

/** Terraform's role. See the module doc: this is a mitigation, not a convenience. */
const DEFAULT_ROLE: MemberRole = 'editor';

export interface ApiKeyCreateFormProps {
  submitting?: boolean;
  fieldErrors?: readonly FieldError[];
  errorMessage?: string;
  onSubmit: (payload: CreateApiKeyPayload) => void;
}

export function ApiKeyCreateForm({
  submitting = false,
  fieldErrors,
  errorMessage,
  onSubmit,
}: ApiKeyCreateFormProps): JSX.Element {
  const [name, setName] = useState('');
  const [role, setRole] = useState<MemberRole>(DEFAULT_ROLE);
  const [expiresInDays, setExpiresInDays] = useState('');

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (submitting) return;
    const trimmedDays = expiresInDays.trim();
    onSubmit({
      name: name.trim(),
      role,
      // Omitted rather than sent as `0` or `null` when the field is blank: the server treats a
      // present-but-unusable `expiresInDays` as a `400` rather than falling back to the default, so a
      // coalesced value would turn "I did not care" into a rejection. A non-numeric entry is passed
      // through as `NaN` deliberately — the server owns the message, and inventing a local one here
      // would be a second validator to keep in step.
      ...(trimmedDays.length === 0 ? {} : { expiresInDays: Number(trimmedDays) }),
    });
  }

  return (
    <form className="entity-form" onSubmit={handleSubmit} noValidate>
      <FormErrorPanel
        message={errorMessage}
        unmapped={unmappedFieldErrors(fieldErrors, MAPPED_PATHS)}
      />

      <FormField
        label="Name"
        path="name"
        errors={fieldErrors}
        hint="How you will recognise this key in the list later — the value itself is never shown again."
        render={(field) => (
          <input
            {...field}
            type="text"
            value={name}
            disabled={submitting}
            onChange={(event) => setName(event.target.value)}
          />
        )}
      />

      <FormField
        label="Role"
        path="role"
        errors={fieldErrors}
        hint="Terraform needs editor; only choose admin if this key must mint other keys."
        render={(field) => (
          <select
            {...field}
            value={role}
            disabled={submitting}
            onChange={(event) => setRole(event.target.value as MemberRole)}
          >
            {MEMBER_ROLES.map((option) => (
              <option key={option} value={option}>
                {ROLE_LABELS[option]}
              </option>
            ))}
          </select>
        )}
      />

      <FormField
        label="Lifetime in days"
        path="expiresInDays"
        errors={fieldErrors}
        // The default is named rather than the request promised: the effective lifetime is
        // `min(requested, configured default, configured maximum)`, so asking for more is capped in
        // silence and a form that said "the key will last N days" would sometimes be wrong.
        hint="Optional. Defaults to the platform maximum, 365 days; a longer request is capped silently."
        render={(field) => (
          <input
            {...field}
            type="number"
            min={1}
            step={1}
            value={expiresInDays}
            disabled={submitting}
            onChange={(event) => setExpiresInDays(event.target.value)}
          />
        )}
      />

      <div className="form-actions">
        <button type="submit" disabled={submitting}>
          Create API key
        </button>
      </div>
    </form>
  );
}
